import 'preact/devtools';
import { localize } from 'i18n/localize';
import { InputBox } from './InputBox';
import { SEARCH_FIELD_ID, SEARCH_BLOCK_ID, handleSubmit, SEARCH_LOCATION_ID, searchQueryFromSuggest } from './signals';
import { useLayoutEffect } from 'preact/hooks';
import { useSignal } from '@preact/signals';
import { Icon } from 'components/atoms/Icon';
import { useIsTabletAndLarger } from 'signals/mediaQuery';
import { cn } from 'utils/cn';
import { filters, type FilterSignalData } from 'signals/filters';

const $t = localize();

const MOBILE_TITLE = $t.searchBox.mobileTitle;
const MOBILE_TITLE_LOCALITY_FOCUSED = $t.searchBox.mobileTitleLocationFocused;

export function SearchBar({
	className,
	focusedInputClassName,
	// The searchbox input might need adjustments on certain pages when viewed on mobile and focused (on mobile when
	// focused the rest of the page is hidden behind white backdrop, title appears above the searchbox, etc.)
	includeLocationFilter = false,
	isFromHeader,
	offersCount,
	placeholder,
	searchBlockId = SEARCH_BLOCK_ID,
	searchFieldId = SEARCH_FIELD_ID,
	searchLocationId = SEARCH_LOCATION_ID,
	ssrFilters,
}: {
	className?: string;
	focusedInputClassName?: string;
	includeLocationFilter?: boolean;
	isFromHeader?: boolean;
	offersCount?: number;
	placeholder?: string;
	searchBlockId?: string;
	searchFieldId?: string;
	searchLocationId?: string;
	ssrFilters?: FilterSignalData;
}) {
	const isInitialized = useSignal(false);
	if (!isInitialized.peek()) {
		isInitialized.value = true;
		if (ssrFilters) filters.value = ssrFilters;
	}

	const isSearchBoxFocused = useSignal(false);
	const showSuggests = useSignal(false);
	const isTablet = useIsTabletAndLarger();

	useLayoutEffect(() => {
		globalThis.document.body.style.overflow = isSearchBoxFocused.peek() && !isTablet.peek() ? 'hidden' : 'auto';
	}, [isSearchBoxFocused.value, isTablet.value]);

	const mobileTitle = useSignal(MOBILE_TITLE);

	return (
		<search
			data-test="search-bar"
			class={cn(
				className,
				isSearchBoxFocused.value && 'search-bar--fullscreen z-[99999] md:z-pageFloater',
				'md:before:content-none md:static md:p-0 md:overflow-visible',
			)}
		>
			<button
				onClick={() => {
					isSearchBoxFocused.value = false;
				}}
				class={cn('fixed right-6 top-5 md:hidden', {
					hidden: !isSearchBoxFocused.value,
				})}
				title={$t.modal.close}
			>
				<Icon type="close"></Icon>
			</button>
			<span
				class={cn('fixed left-6 top-5 md:hidden font-medium ', {
					hidden: !isSearchBoxFocused.value,
				})}
			>
				{mobileTitle.value}
			</span>
			<form
				noValidate
				action={'/'}
				class={cn(
					'h-[calc(100%-68px)] md:h-full',
					isSearchBoxFocused.value ? focusedInputClassName : undefined,
				)}
				onSubmit={(e) => handleSubmit(e, isSearchBoxFocused, showSuggests, searchQueryFromSuggest.peek())}
			>
				<label htmlFor={searchFieldId} class="hidden">
					{$t.searchBox.searchText}
				</label>
				<InputBox
					className={cn('grow', { 'mx-1 md:mx-0': isSearchBoxFocused.value })}
					includeLocationFilter={includeLocationFilter}
					isFromHeader={isFromHeader}
					isSearchBoxFocused={isSearchBoxFocused}
					offersCount={offersCount}
					placeholder={placeholder}
					searchBlockId={searchBlockId}
					searchFieldId={searchFieldId}
					searchLocationId={searchLocationId}
					showSuggests={showSuggests}
					onLocalityFocusIn={() => {
						mobileTitle.value = MOBILE_TITLE_LOCALITY_FOCUSED;
					}}
					onLocalityFocusOut={() => {
						mobileTitle.value = MOBILE_TITLE;
					}}
				/>
				<div
					class={cn(
						'hidden fixed bottom-0 left-0 right-0 pointer-events-none justify-center',
						isSearchBoxFocused.value && !isFromHeader && 'block md:hidden',
					)}
				>
					<button class="flex bg-red-100 hover:bg-red-hover rounded-full items-center justify-center px-5 py-2.5 pointer-events-auto absolute bottom-5 left-8 right-8 font-bold">
						<Icon type="search" size={22} className="text-white font-medium flex-shrink-0" />{' '}
						{offersCount && <span class="text-neutral-white px-1">{$t.searchBox.mobileSearchButton}</span>}
					</button>
					<div class="bg-gradient-to-b from-[#00000000] to-[#ffffff] left-0 right-0 bottom-0 absolute -z-10 h-16"></div>
				</div>
			</form>
		</search>
	);
}
